import { Check, Headphones, Phone, Upload } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader, DescriptionItem } from "@/components/ui/card";
import { StatusDot } from "@/components/ui/badge";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton, Switch } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@/lib/form";
import { generatePassphrase } from "@/lib/passphrase";
import { AVATAR_ACCEPT, avatarFileProblem } from "@/lib/avatar";
import {
  useChangeOwnPassword, useSetOwnAvatar, useSetOwnDisplayName, useSetOwnVoice, useUpdateOwnEndpoint,
} from "@/lib/queries";
import {
  DISPLAY_NAME_MAX, EXTENSION_RE, ROLE_DESCRIPTIONS, ROLE_LABELS, editsOwnPhone, type AppUser,
} from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import { OWN_VOICE_OPTIONS, type VoiceGender } from "@/lib/voice-settings";
import {
  openAudioSetup, useAudioDeviceNames, useMicPermission,
} from "@/softphone/audio-setup";
import { useSoftphone } from "@/softphone/softphone-context";
import { OwnVoiceSection } from "@/features/own-voice";
import { UserAvatar } from "@/features/user-avatar";

const MIN_PASSWORD = 12;

const schema = z
  .object({
    current_password: z.string().min(1, "Required"),
    new_password: z.string().min(MIN_PASSWORD, `At least ${MIN_PASSWORD} characters`),
    confirm: z.string().min(1, "Required"),
  })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    message: "The two passwords do not match",
  });
type FormValues = z.infer<typeof schema>;

export function SettingsPage() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const change = useChangeOwnPassword();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });

  if (!user) return null;

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await change.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      setDone(true);
      form.reset();
      toast.success("Password changed", "Signing you out of this session…");
      // The server revokes every refresh token on a password change — including this tab's — so
      // staying on the page would mean the next request 401s with no explanation. Sign out
      // deliberately, after a beat so the confirmation is readable.
      setTimeout(() => void logout(), 1800);
    } catch (e) {
      setFormError(
        e instanceof ApiError ? e.message : "Could not change your password. Try again.",
      );
    }
  });

  return (
    <Page className="max-w-4xl">
      <PageHeader title="Account" description="Your details and password." />

      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Profile"
            description="Your display name is yours to change. Ask an admin to change the rest."
          />
          <CardBody>
            <dl className="divide-y divide-border">
              <DescriptionItem label="Name">{user.name || user.display_name || "—"}</DescriptionItem>
              <DescriptionItem label="Display name">
                <DisplayNameField user={user} />
              </DescriptionItem>
              <DescriptionItem label="Email">
                <span className="inline-flex items-center gap-1">
                  {user.email}
                  <CopyButton value={user.email} />
                </span>
              </DescriptionItem>
              <DescriptionItem label="Role">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="accent">{ROLE_LABELS[user.role]}</Badge>
                  <span className="text-xs text-text-muted">{ROLE_DESCRIPTIONS[user.role]}</span>
                </div>
              </DescriptionItem>
              <DescriptionItem label="Last sign-in">
                <span className="text-xs text-text-muted">
                  {formatDateTime(user.last_login_at)}
                </span>
              </DescriptionItem>
            </dl>
          </CardBody>
        </Card>

        <PictureCard user={user} />

        {/* Below admin, a person's phone is changed by an admin on their user record, so there
            is nothing here for them to see (ADR-012 in `platform`, amended 2026-09-10). */}
        {editsOwnPhone(user.role) && <PhoneCard user={user} />}

        {/* Only for an account that has a phone: an admin with no endpoint never captures
            audio, so there is nothing to set up. */}
        {user.sip_endpoint_id !== null && <AudioDevicesCard />}

        {/* The same accounts: a voicemail greeting is left on a call, and an account with no phone
            takes none. */}
        {user.sip_endpoint_id !== null && <VoiceCard user={user} />}

        <Card>
          <CardHeader
            title="Password"
            description="Changing it signs you out of every browser, including this one."
          />
          <form onSubmit={onSubmit} noValidate>
            <CardBody className="max-w-md space-y-4">
              {done ? (
                <div className="flex items-center gap-2 rounded-md border border-success-4 bg-success-2 px-3 py-2 text-xs text-success-11">
                  <Check className="size-3.5 shrink-0" aria-hidden />
                  Password changed. Signing you out…
                </div>
              ) : (
                <>
                  {formError && <Callout tone="danger">{formError}</Callout>}
                  <Field
                    label="Current password"
                    htmlFor="current_password"
                    required
                    error={form.formState.errors.current_password?.message}
                  >
                    <Input
                      type="password"
                      autoComplete="current-password"
                      invalid={Boolean(form.formState.errors.current_password)}
                      {...form.register("current_password")}
                    />
                  </Field>
                  <Field
                    label="New password"
                    htmlFor="new_password"
                    required
                    hint={`At least ${MIN_PASSWORD} characters. Length beats punctuation.`}
                    error={form.formState.errors.new_password?.message}
                    action={
                      <button
                        type="button"
                        className="text-xs text-accent-11 hover:underline"
                        onClick={() => {
                          const generated = generatePassphrase();
                          form.setValue("new_password", generated, { shouldValidate: true });
                          form.setValue("confirm", generated, { shouldValidate: true });
                        }}
                      >
                        Generate
                      </button>
                    }
                  >
                    <Input
                      type="password"
                      autoComplete="new-password"
                      invalid={Boolean(form.formState.errors.new_password)}
                      {...form.register("new_password")}
                    />
                  </Field>
                  <Field
                    label="Confirm new password"
                    htmlFor="confirm"
                    required
                    error={form.formState.errors.confirm?.message}
                  >
                    <Input
                      type="password"
                      autoComplete="new-password"
                      invalid={Boolean(form.formState.errors.confirm)}
                      {...form.register("confirm")}
                    />
                  </Field>
                </>
              )}
            </CardBody>
            {!done && (
              <CardFooter>
                <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
                  Change password
                </Button>
              </CardFooter>
            )}
          </form>
        </Card>
      </div>
    </Page>
  );
}


/**
 * Your profile picture (ADR-079 in `platform`), which is why colleagues with the same name can be
 * told apart. It is set once and then only ever replaced. There is no Remove here, and core-api
 * has no route that clears one.
 *
 * The file is checked here only to save a round trip (`lib/avatar.ts`); core-api decodes it and
 * has the last word. The button stays busy until the upload settles, so a second press cannot
 * send another. A session an admin opened as somebody (ADR-040) cannot change their picture, and
 * the card says so instead of offering a button the server would refuse.
 */
function PictureCard({ user }: { user: AppUser }) {
  const toast = useToast();
  const upload = useSetOwnAvatar();
  const chooser = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Truthy rather than `!== null`: against a core-api that predates ADR-079 the field is missing
  // altogether, and that account has no picture either.
  const hasPicture = Boolean(user.avatar_url);
  const openedBy = user.signed_in_by;

  const choose = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again, after fixing whatever was wrong, still fires.
    event.target.value = "";
    if (!file || upload.isPending) return;
    setError(null);
    const problem = avatarFileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    try {
      await upload.mutateAsync(file);
      toast.success(hasPicture ? "Picture changed" : "Picture set");
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Could not upload the picture. Your picture is unchanged.",
      );
    }
  };

  return (
    <Card>
      <CardHeader
        title="Profile picture"
        description="Shown beside your name, so colleagues can tell people with the same name apart. Once set, it can be changed but not removed."
      />
      <CardBody className="space-y-3">
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="flex items-center gap-4">
          <UserAvatar user={user} className="size-16 text-base" />
          <div className="space-y-1.5">
            <input
              ref={chooser}
              type="file"
              accept={AVATAR_ACCEPT}
              className="hidden"
              tabIndex={-1}
              aria-hidden
              onChange={(e) => void choose(e)}
            />
            <Button
              variant="outline"
              size="sm"
              loading={upload.isPending}
              disabled={Boolean(openedBy)}
              onClick={() => chooser.current?.click()}
            >
              <Upload />
              {hasPicture ? "Change picture" : "Upload picture"}
            </Button>
            <p className="text-xs text-text-muted">
              {openedBy
                ? `You are signed in as ${user.display_name || user.email}. Only they can change their picture.`
                : "JPEG, PNG or WebP, up to 10 MB."}
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Your own phone: the number people dial you on, and whether your side of a call is recorded.
 * Rendered for admins and superadmins only (`editsOwnPhone`): below admin, both are set by an
 * admin on the person's user record, and core-api refuses the change from anybody else.
 *
 * Which endpoint is yours is not editable here or anywhere — a call leg names a rep by realm and
 * number and by nothing else, so an endpoint that changed hands would hand its new holder the
 * last holder's call history and recordings (ADR-012 in `platform`).
 *
 * The extension is locked while a call is up. Renaming re-registers the softphone, which would
 * drop a live conversation, and "your change was applied but your call ended" is not a trade
 * anybody would choose.
 */
/**
 * The microphone and speaker this browser uses, and the way back into the setup dialog.
 *
 * Here as well as in the phone's own menu because this is where somebody looks when a colleague
 * says they sound distant: the account page is the place people go to check their own settings,
 * and a device chosen in a dock menu is a device nobody can find again.
 *
 * It describes THIS browser, not the account (`softphone/audio-devices.ts` says why), so the
 * copy says so — the same rep on a different machine sets it up there.
 */
/**
 * The voice that speaks a voicemail greeting in your name (ADR-114 in `platform`): the tenant's
 * female voice or its male one. A greeting says "this is" and your name, so the voice should fit
 * you. Yours to set whatever your role, and an admin can set it for you on the Users page.
 *
 * It saves on the click, as a switch does: there is one choice here and nothing to review.
 */
function VoiceCard({ user }: { user: AppUser }) {
  const toast = useToast();
  const save = useSetOwnVoice();
  const current = user.voice_gender ?? "";
  // The one being saved shows as picked at once, and falls back if the save fails.
  const shown = save.isPending ? (save.variables ?? "") : current;

  const pick = async (value: "" | VoiceGender) => {
    if (value === current || save.isPending) return;
    try {
      await save.mutateAsync(value === "" ? null : value);
      toast.success(
        "Voicemail voice saved",
        value === "" ? "Your team's default voice speaks for you." : `A ${value} voice says your name.`,
      );
    } catch (e) {
      toast.error("Could not save your voice", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Voicemail voice"
        description="The voice that speaks a voicemail greeting in your name. It is used for that and nothing else."
      />
      <CardBody className="space-y-3">
        <div role="radiogroup" aria-label="Voicemail voice" className="grid gap-2 sm:grid-cols-3">
          {OWN_VOICE_OPTIONS.map((option) => {
            const selected = shown === option.value;
            return (
              <button
                key={option.value || "unset"}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={save.isPending}
                onClick={() => void pick(option.value)}
                className={cn(
                  "rounded-md border px-3 py-2 text-left transition-colors disabled:opacity-60",
                  selected
                    ? "border-accent-8 bg-accent-2 ring-2 ring-accent-8/25"
                    : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
                )}
              >
                <span className="block text-sm font-medium text-text">{option.label}</span>
                <span className="mt-0.5 block text-xs text-text-muted">{option.description}</span>
              </button>
            );
          })}
        </div>
        {/* Their own voice speaks instead of the pick above while they have one (ADR-117). */}
        <OwnVoiceSection signedInAs={Boolean(user.signed_in_by)} />
      </CardBody>
    </Card>
  );
}

function AudioDevicesCard() {
  const names = useAudioDeviceNames();
  const permission = useMicPermission();
  return (
    <Card>
      <CardHeader
        title="Microphone and speaker"
        description="What your calls use in this browser. Set once per browser, on each computer you work from."
        action={
          <Button variant="outline" size="sm" onClick={() => openAudioSetup()}>
            <Headphones />
            {permission === "granted" ? "Change" : "Set up"}
          </Button>
        }
      />
      <CardBody>
        {permission === "denied" && (
          <Callout tone="warning" className="mb-3">
            This browser is blocking the microphone, so callers cannot hear you. Set up shows how
            to turn it back on.
          </Callout>
        )}
        {permission === "prompt" && (
          <Callout tone="info" className="mb-3">
            Your browser has not been asked for the microphone yet. Doing it now means a call
            never arrives with a permission bar over it.
          </Callout>
        )}
        <dl className="divide-y divide-border">
          <DescriptionItem label="Microphone">{names.mic}</DescriptionItem>
          <DescriptionItem label="Speaker">{names.speaker}</DescriptionItem>
        </dl>
      </CardBody>
    </Card>
  );
}

/**
 * The name you go by (ADR-172 in `platform`), the one part of your profile that is yours to change:
 * an English name you chose, say. It is the name on your calls, the floor and the reports, and the
 * one your voicemail greetings say. Your real name stays as an admin keeps it. Saved empty, the
 * name you go by is your real name again.
 */
function DisplayNameField({ user }: { user: AppUser }) {
  const toast = useToast();
  const save = useSetOwnDisplayName();
  const current = user.display_name ?? "";
  const [value, setValue] = React.useState(current);
  const [error, setError] = React.useState<string | null>(null);

  // Re-seed whenever the account reloads, so a save (or an admin's change) shows what is stored.
  React.useEffect(() => {
    setValue(user.display_name ?? "");
  }, [user.display_name]);

  const dirty = value.trim() !== current;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const saved = await save.mutateAsync(value.trim() || null);
      toast.success("Display name saved", saved.display_name ?? undefined);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save. Try again.");
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Display name"
          aria-describedby="own-display-name-hint"
          className="max-w-xs"
          maxLength={DISPLAY_NAME_MAX}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        {dirty && (
          <Button type="submit" size="sm" loading={save.isPending}>
            Save
          </Button>
        )}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-danger-11">
          {error}
        </p>
      ) : (
        <p id="own-display-name-hint" className="text-xs text-text-muted">
          The name on your calls and your voicemail greetings. Left empty, it is your name.
        </p>
      )}
    </form>
  );
}

function PhoneCard({ user }: { user: AppUser }) {
  const toast = useToast();
  const { reload } = useAuth();
  const { registration, calls, connect, disconnect } = useSoftphone();
  const save = useUpdateOwnEndpoint();

  const current = user.sip_endpoint_extension ?? "";
  const [extension, setExtension] = React.useState(current);
  const [recordCalls, setRecordCalls] = React.useState(user.sip_endpoint_record_calls ?? true);
  const [error, setError] = React.useState<string | null>(null);

  // Re-seed whenever the account reloads, so a save (or an admin's change) is reflected rather
  // than leaving the field showing what was typed before.
  React.useEffect(() => {
    setExtension(user.sip_endpoint_extension ?? "");
    setRecordCalls(user.sip_endpoint_record_calls ?? true);
  }, [user.sip_endpoint_extension, user.sip_endpoint_record_calls]);

  if (!user.sip_endpoint_id) {
    return (
      <Card>
        <CardHeader title="Your phone" />
        <CardBody>
          <p className="text-xs text-text-muted">
            No endpoint is assigned to you, so the console has no phone to show. Ask an admin —
            they can give you one from your user record.
          </p>
        </CardBody>
      </Card>
    );
  }

  const trimmed = extension.trim();
  const renaming = trimmed !== current;
  const onACall = calls.length > 0;
  const invalid = trimmed.length > 0 && !EXTENSION_RE.test(trimmed);
  const dirty = renaming || recordCalls !== (user.sip_endpoint_record_calls ?? true);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (invalid || !trimmed) {
      setError("An extension is exactly four digits, for example 1004.");
      return;
    }
    try {
      await save.mutateAsync({
        ...(renaming ? { extension: trimmed } : {}),
        record_calls: recordCalls,
      });
      await reload();
      if (renaming) {
        // The switch authenticates the extension, so the live registration is now on a name
        // that no longer exists. Take the line down and bring it back up on the new one rather
        // than leaving a phone that looks connected and rings for nobody.
        disconnect();
        await connect();
        toast.success("Extension changed", `Your phone is now ${trimmed}.`);
      } else {
        toast.success("Phone updated");
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save. Try again.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Your phone"
        description="This endpoint is yours for as long as your account exists. Nobody else can be given it."
      />
      <form onSubmit={submit} noValidate>
        <CardBody className="space-y-4">
          {error && <Callout tone="danger">{error}</Callout>}

          <dl className="divide-y divide-border">
            <DescriptionItem label="Domain">
              <span className="font-mono text-xs">{user.sip_endpoint_domain_name}</span>
            </DescriptionItem>
            <DescriptionItem label="Status">
              <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
                <StatusDot
                  state={registration === "registered" ? true : registration === "failed" ? false : null}
                  label={REGISTRATION_LABELS[registration]}
                />
                {REGISTRATION_LABELS[registration]}
              </span>
            </DescriptionItem>
          </dl>

          <Field
            label="Extension"
            htmlFor="extension"
            required
            hint={
              onACall
                ? "Locked while you are on a call — changing it re-registers your phone."
                : "Four digits. What colleagues dial you on, and what your phone signs in as."
            }
            error={invalid ? "Exactly four digits, for example 1004" : undefined}
            className="max-w-xs"
          >
            <Input
              className="font-mono"
              inputMode="numeric"
              autoComplete="off"
              value={extension}
              disabled={onACall}
              invalid={invalid}
              onChange={(e) => setExtension(e.target.value)}
            />
          </Field>

          {renaming && !invalid && (
            <Callout tone="warning">
              Your phone will sign back in as {trimmed}. A desk phone registered on{" "}
              {current} stops until it is reconfigured, and calls you made before the change stay
              filed under {current}.
            </Callout>
          )}

          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5">
            <div>
              <p className="text-xs font-medium text-text">Record my calls</p>
              <p className="text-xs text-text-muted">
                {/* Both directions, because that is the part people get wrong: it is not
                    "record what I dial", it is "record this phone". */}
                Your own side of calls you place and calls you are rung for. Takes effect on your
                next call.
              </p>
            </div>
            <Switch
              checked={recordCalls}
              onCheckedChange={setRecordCalls}
              aria-label="Record my calls"
            />
          </div>
        </CardBody>
        <CardFooter>
          <Button type="submit" variant="default" disabled={!dirty} loading={save.isPending}>
            <Phone />
            Save phone
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

const REGISTRATION_LABELS: Record<string, string> = {
  idle: "Not connected",
  connecting: "Connecting…",
  registered: "Registered",
  standby: "On another tab",
  failed: "Not registered",
};
